import { useEffect, useRef, useState, useCallback } from 'react';

interface AudioContextHook {
  audioContext: AudioContext | null;
  playSound: (soundUrl: string, volume: number) => Promise<void>;
  scheduleSound: (soundUrl: string, volume: number, whenTimeSec: number) => Promise<void>;
  preloadClickSet: (style: string) => Promise<void>;
  setReverbAmount: (amount: number) => void;
  reverbAmount: number;
}

export const useAudioContext = (): AudioContextHook => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const convolverNodeRef = useRef<ConvolverNode | null>(null);
  const dryGainRef = useRef<GainNode | null>(null);
  const wetGainRef = useRef<GainNode | null>(null);
  const [reverbAmount, setReverbAmountState] = useState(0.0); // Default 30% reverb
  const audioBuffersRef = useRef<Map<string, AudioBuffer>>(new Map());

  // Create impulse response for reverb
  const createImpulseResponse = useCallback((context: AudioContext, duration: number = 2, decay: number = 2): AudioBuffer => {
    const sampleRate = context.sampleRate;
    const length = sampleRate * duration;
    const impulse = context.createBuffer(2, length, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        const n = length - i;
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(n / length, decay);
      }
    }
    
    return impulse;
  }, []);

  // Initialize audio context and nodes
  useEffect(() => {
    const initAudioContext = async () => {
      try {
        const context = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = context;

        // Create gain node for volume control
        const gainNode = context.createGain();
        gainNodeRef.current = gainNode;

        // Create convolver node for reverb
        const convolverNode = context.createConvolver();
        convolverNodeRef.current = convolverNode;
        
        // Create impulse response for reverb
        const impulseResponse = createImpulseResponse(context);
        convolverNode.buffer = impulseResponse;

        // Create dry and wet gain nodes for reverb mixing
        const dryGain = context.createGain();
        const wetGain = context.createGain();
        dryGainRef.current = dryGain;
        wetGainRef.current = wetGain;

        // Set initial reverb amount (use current state value)
        const currentReverbAmount = reverbAmount;
        dryGain.gain.value = 1 - currentReverbAmount;
        wetGain.gain.value = currentReverbAmount;

        // Connect audio graph: source -> gain -> split to dry/wet -> destination
        gainNode.connect(dryGain);
        gainNode.connect(convolverNode);
        convolverNode.connect(wetGain);
        
        dryGain.connect(context.destination);
        wetGain.connect(context.destination);

      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    };

    initAudioContext();

    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createImpulseResponse]); // reverbAmount removed to prevent audio context recreation

  // Update reverb amount
  const setReverbAmount = useCallback((amount: number) => {
    setReverbAmountState(amount);
    
    if (dryGainRef.current && wetGainRef.current) {
      dryGainRef.current.gain.value = 1 - amount;
      wetGainRef.current.gain.value = amount;
    }
  }, []);

  // Load and cache audio buffer
  const loadAudioBuffer = useCallback(async (url: string): Promise<AudioBuffer | null> => {
    if (!audioContextRef.current) return null;

    // Check if already cached
    const cached = audioBuffersRef.current.get(url);
    if (cached) return cached;

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Cache the buffer
      audioBuffersRef.current.set(url, audioBuffer);
      return audioBuffer;
    } catch (error) {
      console.error('Failed to load audio:', error);
      return null;
    }
  }, []);

  // Preload a set of click samples for a given style
  const preloadClickSet = useCallback(async (style: string) => {
    const lower = style.toLowerCase();
    const ext = lower === 'drop' ? '.wav' : '.mp3';
    const urls = [
      `/click_${lower}_hi${ext}`,
      `/click_${lower}_mid${ext}`,
      `/click_${lower}_lo${ext}`,
    ];
    await Promise.all(urls.map((u) => loadAudioBuffer(u)));
  }, [loadAudioBuffer]);

  // Play sound with reverb
  const playSound = useCallback(async (soundUrl: string, volume: number = 1) => {
    if (!audioContextRef.current || !gainNodeRef.current) return;

    // Resume context if suspended (required for user interaction)
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const audioBuffer = await loadAudioBuffer(soundUrl);
    if (!audioBuffer) return;

    // Create buffer source
    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;

    // Set volume
    gainNodeRef.current.gain.value = volume;

    // Connect source to gain node (which is already connected to the reverb chain)
    source.connect(gainNodeRef.current);

    // Play the sound
    source.start();
  }, [loadAudioBuffer]);

  // Schedule sound at a precise AudioContext time
  const scheduleSound = useCallback(async (soundUrl: string, volume: number = 1, whenTimeSec: number) => {
    if (!audioContextRef.current || !gainNodeRef.current) return;

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const audioBuffer = await loadAudioBuffer(soundUrl);
    if (!audioBuffer) return;

    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    gainNodeRef.current.gain.value = volume;
    source.connect(gainNodeRef.current);
    source.start(whenTimeSec);
  }, [loadAudioBuffer]);

  return {
    audioContext: audioContextRef.current,
    playSound,
    scheduleSound,
    preloadClickSet,
    setReverbAmount,
    reverbAmount,
  };
};
